import React, { useState, forwardRef, useImperativeHandle } from "react";
import { Box, Typography, TextField, Button, Divider } from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";

const ProgressTracker = forwardRef(
  (
    { sections = {}, overallCompletion = {}, formData = {}, sectionData = {} },
    ref
  ) => {
    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      openNoteDialog: (sectionId) => {
        console.log("openNoteDialog sectionData", sectionData);
        // This method is no longer used since we're using inline notes
        console.log("Note dialog functionality moved to inline notes");
      },
    }));

    // Helper function to normalize sections to array format
    const normalizeSections = (sections) => {
      if (!sections) return [];

      // If it's already an array, return as-is
      if (Array.isArray(sections)) {
        return sections;
      }

      // If it's an object (new format), convert to array
      if (typeof sections === "object") {
        return Object.values(sections);
      }

      return [];
    };

    // Get section status from the status field in the section object
    const getSectionStatus = (section) => {
      // Use the status field from the section object
      const status = section.status;

      if (!status) {
        return "no_status"; // Gray check mark
      }

      switch (status.toLowerCase()) {
        case "completed":
          return "completed"; // Green check mark
        case "submitted_with_note":
          return "submitted_with_note"; // Yellow check mark
        default:
          return "no_status"; // Gray check mark for any other value
      }
    };

    const sectionsArray = normalizeSections(sections);

    if (sectionsArray.length === 0) {
      return null;
    }

    // Calculate overall completion based on status field
    const completedSections = sectionsArray.filter((section) => {
      const status = getSectionStatus(section);
      return status === "completed" || status === "submitted_with_note";
    }).length;

    const overallPercentComplete = Math.round(
      (completedSections / sectionsArray.length) * 100
    );

    return (
      <>
        <Box sx={{ py: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "#333333",
                fontWeight: 600,
                fontSize: "18px",
              }}
            >
              Completion Status Progress Tracker
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  color: "#666666",
                  fontSize: "16px",
                  fontWeight: 500,
                }}
              >
                {completedSections} of {sectionsArray.length} sections completed
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: "#4f46e5",
                  fontWeight: 700,
                  fontSize: "24px",
                }}
              >
                {overallPercentComplete}%
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ mb: 3 }} />

          <Box 
            sx={{ 
              display: "flex", 
              flexWrap: "wrap", 
              alignItems: "flex-start", 
              gap: 3, 
              mb: 4,
              justifyContent: { xs: "center", sm: "flex-start" } // Center on mobile, left-align on larger screens
            }}
          >
            {/* Ready to Submit Status */}
            {/* <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                height: "120px", // Match the height of other items
                justifyContent: "space-between",
                minWidth: "80px",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#333333",
                  fontSize: "12px",
                  fontWeight: 500,
                  textAlign: "center",
                  maxWidth: "80px",
                  height: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 1.2,
                }}
              >
                Ready to Submit
              </Typography>
              <Box
                sx={{
                  height: "24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {overallPercentComplete === 100 ? (
                  <CheckCircleIcon sx={{ color: "#4ade80", fontSize: "24px" }} />
                ) : (
                  <RadioButtonUncheckedIcon
                    sx={{ color: "#d1d5db", fontSize: "24px" }}
                  />
                )}
              </Box>
              <Box
                sx={{
                  height: "40px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "#666666",
                    fontSize: "10px",
                    textAlign: "center",
                  }}
                >
                  Status
                </Typography>
              </Box>
            </Box> */}

            {/* Responsive Divider - Show only on larger screens when items are in a single row */}
            {/* <Box
              sx={{
                display: { xs: "none", md: sectionsArray.length <= 6 ? "block" : "none" },
                alignSelf: "center",
              }}
            >
              <Divider orientation="vertical" sx={{ height: "100px" }} />
            </Box> */}

            {/* Section Progress */}
            {sectionsArray.map((section) => {
              const sectionId = section.id || section.sectionId;
              const sectionTitle =
                section.title || section.sectionType || `Section ${sectionId}`;
              const status = getSectionStatus(section);

              // Determine icon and color based on status
              let icon;
              let color;
              let clickable = false;

              switch (status) {
                case "completed":
                  icon = (
                    <CheckCircleIcon
                      sx={{ color: "#4ade80", fontSize: "24px" }}
                    />
                  );
                  color = "#4ade80";
                  break;
                case "submitted_with_note":
                  icon = (
                    <WarningIcon
                      sx={{
                        color: "#f59e0b",
                        fontSize: "24px",
                        cursor: "pointer",
                      }}
                    />
                  );
                  color = "#f59e0b";
                  clickable = true;
                  break;
                default:
                  icon = (
                    <RadioButtonUncheckedIcon
                      sx={{ color: "#d1d5db", fontSize: "24px" }}
                    />
                  );
                  color = "#d1d5db";
              }

              return (
                <Box
                  key={section.uniqueKey || sectionId}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    height: "120px", // Fixed height for consistency
                    justifyContent: "space-between", // Distribute content evenly
                    minWidth: "80px", // Ensure consistent width too
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#333333",
                      fontSize: "12px",
                      fontWeight: 500,
                      textAlign: "center",
                      maxWidth: "80px",
                      height: "32px", // Fixed height for title area
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      lineHeight: 1.2,
                    }}
                  >
                    {sectionTitle}
                  </Typography>
                  <Box
                    onClick={
                      clickable
                        ? () => {
                            console.log("[ProgressTracker] Section clicked", {
                              sectionId,
                              clickable,
                            });
                            // Note dialog functionality moved to inline notes in JsonFormRenderer
                            console.log(
                              "Note functionality moved to inline notes"
                            );
                          }
                        : undefined
                    }
                    sx={{ 
                      cursor: clickable ? "pointer" : "default",
                      height: "24px", // Fixed height for icon area
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {icon}
                  </Box>
                  <Box
                    sx={{
                      height: "40px", // Fixed height for status area
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#666666",
                        fontSize: "10px",
                        textAlign: "center",
                      }}
                    >
                      Status: {section.status || "No Status"}
                    </Typography>
                    {status === "submitted_with_note" && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#f59e0b",
                          fontSize: "9px",
                          textAlign: "center",
                          mt: 0.5,
                        }}
                      >
                        Note Added
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>


        </Box>

        {/* Note Dialog */}
        {/* The Dialog component and its related state/functions are removed */}
      </>
    );
  }
);

ProgressTracker.displayName = "ProgressTracker";

export default ProgressTracker;
